/* eslint-disable react/display-name */
import { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';

import { Input, Row } from 'components/Blocks';

import { getLastIndex } from 'utils/array';
import { onlyNumericChars } from 'utils/text';
import { cn } from 'styles';

export type AuthCodeProps = {
  disabled?: boolean;
  hasError?: boolean;
  onChange: (res: string) => void;
};

export type AuthCodeRef = {
  focus: () => void;
  clear: () => void;
};

type ValueMapping = Record<string, string>;

function validateOnlyNumbers(value: string): boolean {
  const numberRegex = /^[0-9]*$/g;
  const allNumbers = numberRegex.test(value);

  return allNumbers;
}

const renderArray = Array.from({ length: 6 }, (_, i) => i);
const lastIndex = getLastIndex(renderArray);

const initialValues = renderArray.reduce(
  (acc, next) => ({
    ...acc,
    [next]: '',
  }),
  {},
);

export const OTPInput = forwardRef<AuthCodeRef, AuthCodeProps>(
  ({ disabled, onChange, hasError }, ref) => {
    const inputsRef = useRef<Array<HTMLInputElement>>([]);

    const [values, setValues] = useState<ValueMapping>(initialValues);

    const sendResult = (updatedValues: ValueMapping): void => {
      const res = renderArray.map((index) => updatedValues[index]).join('');
      onChange(res);
    };

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputsRef.current[0]?.focus();
      },

      clear: () => {
        setValues(initialValues);
        sendResult(initialValues);
      },
    }));

    useEffect(() => {
      inputsRef.current[0]?.focus();
    }, []);

    const focusNextInput = (index: number): void => {
      const nextIndex = index + 1;

      if (nextIndex <= lastIndex) inputsRef.current[nextIndex]?.focus();
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>, index: number): void => {
      const {
        target: { value },
      } = e;

      if (value.length > 1 || !validateOnlyNumbers(value)) return;

      const updated = { ...values, [index]: value };
      setValues(updated);

      if (value) focusNextInput(index);

      sendResult(updated);
    };

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number): void => {
      const { key } = e;
      const currentValue = values[index];

      if (currentValue === key) return focusNextInput(index);

      if (key !== 'Backspace') return;

      if (currentValue || !index) return;

      e.preventDefault();

      const siblingIndex = index - 1;

      const sibling = inputsRef.current[siblingIndex];

      sibling.focus();

      const updated = { ...values, [siblingIndex]: '' };
      setValues(updated);

      sendResult(updated);
    };

    const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
      e.target.select();
    };

    const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
      const pastedValue = e.clipboardData.getData('Text') || '';

      const possibleCode = onlyNumericChars(pastedValue).slice(0, 6);

      if (!possibleCode) return;

      const valuesPasted = possibleCode.split('').reduce(
        (acc, code, index) => ({
          ...acc,
          [index]: code,
        }),
        {},
      );

      const updated = { ...values, ...valuesPasted };
      setValues(updated);

      sendResult(updated);

      e.preventDefault();
    };

    return (
      <Row alignItems="center" justifyContent="center" gap="3" className="small_phone:gap-1">
        {renderArray.map((index) => (
          <Input
            key={`code-number-${index}`}
            value={values[index]}
            onChange={(e) => handleOnChange(e, index)}
            onKeyDown={(e) => handleOnKeyDown(e, index)}
            onFocus={handleOnFocus}
            onPaste={handleOnPaste}
            ref={(el: HTMLInputElement) => {
              inputsRef.current[index] = el;
            }}
            maxLength={1}
            disabled={disabled}
            type="text"
            autoComplete="off"
            pattern="\d*"
            className={cn(
              'size-11 rounded-sm text-center text-2xl uppercase text-primary small_phone:size-9',
              hasError && 'border-destructive',
            )}
          />
        ))}
      </Row>
    );
  },
);
